import React from 'react';
import { Button, Chip, Textarea } from '@heroui/react';
import { RiEditLine, RiStickyNoteLine } from '@remixicon/react';

import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import type { ContactMessage } from '@/types';

interface ContactMessageThreadProps {
  message: ContactMessage;
  onEdit: (payload: { messageId: number; body: string; isNote?: boolean; parentMessageId?: number }) => Promise<void>;
  currentUserId?: number | null;
}

export function ContactMessageThread({ message, onEdit, currentUserId }: ContactMessageThreadProps) {
  const sortedChildren = React.useMemo(() => {
    const replies = message.replies ?? [];
    const notes = message.notes ?? [];
    return [...replies, ...notes].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [message.replies, message.notes]);

  const parentId = message.id;

  return (
    <div className="space-y-4">
      <ThreadItem
        message={message}
        isRoot
        canEdit={Boolean(message.is_admin && message.author?.id && message.author?.id === currentUserId)}
        onEdit={onEdit}
        parentMessageId={parentId}
      />

      {sortedChildren.length > 0 ? (
        <div className="space-y-3 border-l-2 border-default-200 pl-4">
          {sortedChildren.map((reply) => (
            <ThreadItem
              key={reply.id}
              message={reply}
              canEdit={Boolean(reply.is_admin && reply.author?.id && reply.author?.id === currentUserId)}
              onEdit={onEdit}
              parentMessageId={parentId}
            />
          ))}
        </div>
      ) : (
        <div className="text-default-500 rounded-large border border-dashed border-default-200 p-4 text-center text-sm">
          No replies or notes yet. Start the conversation below.
        </div>
      )}
    </div>
  );
}

interface ThreadItemProps {
  message: ContactMessage;
  isRoot?: boolean;
  canEdit?: boolean;
  onEdit: (payload: { messageId: number; body: string; isNote?: boolean; parentMessageId?: number }) => Promise<void>;
  parentMessageId?: number;
}

function ThreadItem({ message, isRoot = false, canEdit = false, onEdit, parentMessageId }: ThreadItemProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(message.body);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isEditing) {
      setDraft(message.body);
    }
  }, [message.body, isEditing]);

  const authorName =
    message.author?.first_name || message.author?.last_name
      ? `${message.author?.first_name ?? ''} ${message.author?.last_name ?? ''}`.trim()
      : message.name || 'Unknown';
  const authorEmail = message.author?.email || message.email || undefined;

  const handleSave = React.useCallback(async () => {
    if (!draft.trim()) {
      setError('Message cannot be empty');
      return;
    }
    setError(null);

    try {
      setIsSaving(true);
      await onEdit({ messageId: message.id, body: draft.trim(), isNote: message.is_note, parentMessageId });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [draft, message.id, message.is_note, onEdit, parentMessageId]);

  const highlightClass = isRoot
    ? 'bg-content2 border border-default-200'
    : message.is_note
      ? 'bg-yellow-50 border border-yellow-200'
      : message.is_admin
        ? 'bg-blue-50 border border-blue-200'
        : 'bg-content1 border border-default-200';

  return (
    <div className={`${highlightClass} rounded-large p-4 transition-colors`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-default-900 font-semibold">{authorName || 'Unknown'}</p>
          {authorEmail && <p className="text-default-500 text-sm">{authorEmail}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-default-500">
          {message.is_note && (
            <Chip color="warning" size="sm" startContent={<RiStickyNoteLine className="h-3 w-3" />} variant="flat">
              Internal Note
            </Chip>
          )}
          {message.is_admin && <Chip size="sm" color="primary" variant="flat">Admin</Chip>}
          <DateTimeDisplay dateString={message.created_at} />
          {message.edited_at && <span className="text-default-400 italic">Edited</span>}
        </div>
      </div>

      <div className="mt-3">
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={draft}
              onValueChange={setDraft}
              minRows={isRoot ? 6 : 4}
              isInvalid={Boolean(error)}
              errorMessage={error ?? undefined}
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="light" onPress={() => setIsEditing(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button color="primary" onPress={handleSave} isLoading={isSaving}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-default-800 whitespace-pre-wrap text-sm">{message.body}</p>
        )}
      </div>

      {!isEditing && canEdit && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" variant="light" startContent={<RiEditLine className="h-4 w-4" />} onPress={() => setIsEditing(true)}>
            Edit
          </Button>
        </div>
      )}
    </div>
  );
}

